import { useState } from "react";
import { ChevronDown, ChevronRight, Plus, Radio } from "lucide-react";
import { useAuthStore } from "@/store/auth";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import {
  useSentSystemMessages,
  useSystemMessageReceipts,
} from "../hooks/useSystemMessages";
import { ComposeSystemMessageModal } from "../components/ComposeSystemMessageModal";
import { canSendSystemMessages } from "../types";
import type { SystemMessage } from "../types";

function formatDateTime(value: string): string {
  return new Date(value).toLocaleString("pl-PL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function Receipts({ id }: { id: string }) {
  const { data, isLoading } = useSystemMessageReceipts(id, true);

  if (isLoading) {
    return (
      <div className="flex justify-center py-4">
        <Spinner size="sm" />
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="grid gap-4 border-t border-[var(--color-border)] px-4 py-3 sm:grid-cols-2">
      <div>
        <p className="text-xs font-mono uppercase tracking-wider text-[var(--color-text-muted)]">
          Przeczytali ({data.read_count})
        </p>
        <ul className="mt-1 space-y-0.5">
          {data.read.map((r) => (
            <li key={r.id} className="text-sm text-[var(--color-text-primary)]">
              {r.name}
              <span className="ml-2 text-xs text-[var(--color-text-secondary)]">
                {r.read_at ? formatDateTime(r.read_at) : ""}
              </span>
            </li>
          ))}
          {data.read.length === 0 && (
            <li className="text-sm text-[var(--color-text-secondary)]">
              Jeszcze nikt.
            </li>
          )}
        </ul>
      </div>
      <div>
        <p className="text-xs font-mono uppercase tracking-wider text-[var(--color-text-muted)]">
          Jeszcze nie przeczytali ({data.unread_count})
        </p>
        <ul className="mt-1 space-y-0.5">
          {data.unread.map((r) => (
            <li key={r.id} className="text-sm text-[var(--color-text-primary)]">
              {r.name}
              {r.branch_name && (
                <span className="ml-2 text-xs text-[var(--color-text-secondary)]">
                  {r.branch_name}
                </span>
              )}
            </li>
          ))}
          {data.unread.length === 0 && (
            <li className="text-sm text-[var(--color-text-secondary)]">
              Wszyscy potwierdzili.
            </li>
          )}
        </ul>
      </div>
    </div>
  );
}

function MessageCard({ message }: { message: SystemMessage }) {
  const [open, setOpen] = useState(false);
  const allRead =
    message.recipient_count > 0 && message.read_count === message.recipient_count;

  return (
    <article className="border border-[var(--color-border)] bg-[var(--color-bg-surface)]">
      <div className="px-4 py-3">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">
            {message.title}
          </h3>
          <span className="text-xs text-[var(--color-text-secondary)]">
            {message.sender_name} · {formatDateTime(message.created_at)}
          </span>
        </div>
        <p className="mt-2 whitespace-pre-wrap text-sm text-[var(--color-text-secondary)]">
          {message.body}
        </p>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="mt-3 flex items-center gap-1 text-xs text-[var(--color-text-secondary)] hover:text-[var(--color-primary)]"
        >
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          <span className={allRead ? "text-green-600 dark:text-green-400" : ""}>
            Przeczytane: {message.read_count} z {message.recipient_count}
          </span>
        </button>
      </div>
      {open && <Receipts id={message.id} />}
    </article>
  );
}

/** „Wiadomości systemowe" — wysyłka i potwierdzenia odczytu.
 *
 *  Strona dla nadawców (dyrektor / CEO / dev). Odbiorcy nie mają tu nic do
 *  roboty — wiadomość dociera do nich blokującym oknem (SystemMessagePopupHost). */
export function SystemMessagesPage() {
  const user = useAuthStore((s) => s.user);
  const canSend = canSendSystemMessages(user);
  const [composeOpen, setComposeOpen] = useState(false);
  const { data: messages = [], isLoading } = useSentSystemMessages(canSend);

  if (!canSend) {
    return (
      <p className="text-sm text-[var(--color-text-secondary)]">
        Ta sekcja jest dostępna dla dyrekcji.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Radio size={18} className="text-[var(--color-primary)]" />
          <h1 className="text-lg font-bold text-[var(--color-text-primary)]">
            Wiadomości systemowe
          </h1>
        </div>
        <Button type="button" onClick={() => setComposeOpen(true)}>
          <Plus size={14} className="mr-1" />
          Nowa wiadomość
        </Button>
      </div>

      <p className="text-sm text-[var(--color-text-secondary)]">
        Wiadomość wyświetli się wybranym pracownikom w oknie, którego nie da się
        zamknąć przez 30 sekund. Znika dopiero po kliknięciu „Oznacz jako
        przeczytane" — poniżej widzisz, kto już to zrobił.
      </p>

      {isLoading ? (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      ) : messages.length === 0 ? (
        <p className="text-sm text-[var(--color-text-secondary)]">
          Nie wysłano jeszcze żadnej wiadomości.
        </p>
      ) : (
        <div className="flex flex-col gap-3">
          {messages.map((m) => (
            <MessageCard key={m.id} message={m} />
          ))}
        </div>
      )}

      <ComposeSystemMessageModal
        open={composeOpen}
        onClose={() => setComposeOpen(false)}
      />
    </div>
  );
}
